import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";

// Extend dayjs with plugins
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);

export { dayjs };

// Date format constants
export const FORMAT_TYPEABLE_DATE = "MM / DD / YYYY"; // For typeable date picker
export const FORMAT_SIMPLE_MONTH_DAY = "M/D";
export const FORMAT_TYPEABLE_DATE_PLACEHOLDER = "mm / dd / yyyy";
export const FORMAT_FILENAME_DATE = "YYYY-MM-DD";
export const FORMAT_API_DATETIME = "YYYY-MM-DDTHH:mm:ssZ"; // For API datetime submissions with timezone offset
export const FORMAT_DATE_PICKER_V2 = "PPP"; // For date picker v2 button display

/**
 * Gets the day of the week or date string for a given date
 *
 * @param date - Date string, Date object, or null
 * @returns Day of the week or date string or "No Date" if input is null/undefined
 */
const getDateString = (date?: string | Date | null) => {
  if (!date) return "No Date";

  const inputDate = dayjs(date);
  const now = dayjs();

  if (inputDate.isSame(now, "day")) return "Today";
  if (inputDate.isSame(now.subtract(1, "day"), "day")) return "Yesterday";

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  if (inputDate.isSame(now, "week")) return days[inputDate.day()];
  return inputDate.format("MMM DD");
};

/**
 * Formats a date string or Date object into a readable date format
 *
 * Format string: "MMM DD, YYYY"
 *
 * Example: "Dec 01, 2024"
 *
 * @param date - Date string, Date object, or null
 * @returns Formatted date string or "No Date" if input is null/undefined
 */
export const formatDate = (date?: string | Date | null) =>
  date ? dayjs(date).format("MMM DD, YYYY") : "No Date";

/**
 * Formats a date string or Date object into a readable date and time format
 *
 * Format string: "MMM DD, YYYY - h:mm a"
 *
 * Example: "Mar 21, 2024 - 10:00 am"
 *
 * @param date - Date string, Date object, or null
 * @returns Formatted date and time string or "No Time" if input is null/undefined
 */
export const formatDateAndTime = (date?: string | Date | null) =>
  date ? dayjs(date).format("MMM DD, YYYY - h:mm a") : "No Time";

/**
 * Formats a date string or Date object into a readable date and time format
 *
 * Format string: "MMM DD, h:mm a"
 *
 * Example: "Mar 21, 10:00 am"
 *
 * @param date - Date string, Date object, or null
 * @returns Formatted date and time string or "No Time" if input is null/undefined
 */
export const formatTime = (date?: string | Date | null) =>
  date ? dayjs(date).format("MMM DD, h:mm a") : "No Time";

/**
 * Formats a date string or Date object into a readable date and time format with timezone
 *
 * Format string: "MMM DD, YYYY - h:mm A z"
 *
 * Example: "Jan 01, 2024 - 10:02 AM PST"
 *
 * @param date - Date string, Date object, or null
 * @returns Formatted date, time and timezone string or "No Date" if input is null/undefined
 */
export const formatDateTimeWithZone = (date?: string | Date | null) =>
  date
    ? `${dayjs(date).tz(dayjs.tz.guess()).format("MMM DD, YYYY - h:mm A z")}`
    : "No Date";

/**
 * Formats a date string or Date object into a relative time string
 *
 * Examples: "2 days ago", "Last week", "2 months ago", "Last year"
 *
 * @param date - Date string, Date object, or null
 * @returns Relative time string or "No Date" if input is null/undefined
 */
export const formatRelativeDate = (date?: string | Date | null) => {
  if (!date) return "No Date";

  // Use calendar days, not 24-hour periods
  const now = dayjs().startOf("day");
  const then = dayjs(date).startOf("day");
  const diff = now.diff(then, "day");

  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff} days ago`;
  if (diff < 14) return "Last week";
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  if (diff < 60) return "Last month";
  if (diff < 365) return `${Math.floor(diff / 30)} months ago`;
  if (diff < 730) return "Last year";
  return `${Math.floor(diff / 365)} years ago`;
};

/**
 * Returns the difference in calendar days between two dates
 *
 * Formats a date string or Date object into a relative time string
 *
 * Examples: "Today, 11:00 AM", "Yesterday, 11:00 AM", "Monday, 11:00 AM"
 *
 * @param date - Date string, Date object, or null
 * @returns Relative time string or "No Time" if input is null/undefined
 */
export const formatRelativeTime = (date?: string | Date | null) => {
  if (!date) return "No Time";

  const inputDate = dayjs(date);
  const now = dayjs();

  if (inputDate.isSame(now, "year")) {
    return `${getDateString(date)}, ${inputDate.format("h:mm a")}`;
  } else {
    return formatDate(date);
  }
};

/**
 * Collection of date utility functions
 */
export const differenceInCalendarDays = (
  dateLeft: Date,
  dateRight: Date,
): number => {
  // Only the date part is considered, not the time
  const left = dayjs(dateLeft).startOf("day");
  const right = dayjs(dateRight).startOf("day");
  return left.diff(right, "day");
};

// Move dateUtils export to the end
export const dateUtils = {
  /**
   * Checks if a given date is today
   * @param date - Date string or Date object
   * @returns boolean indicating if the date is today
   */
  isToday: (date: string | Date) => dayjs(date).isSame(dayjs(), "day"),

  /**
   * Checks if a given date is in the future
   * @param date - Date string or Date object
   * @returns boolean indicating if the date is in the future
   */
  isFuture: (date: string | Date) => dayjs(date).isAfter(dayjs()),

  /**
   * Checks if a given date is in the past
   * @param date - Date string or Date object
   * @returns boolean indicating if the date is in the past
   */
  isPast: (date: string | Date) => dayjs(date).isBefore(dayjs()),

  /**
   * Adds a specified number of days to a given date
   * @param date - Date string or Date object
   * @param days - Number of days to add
   * @returns New Date object with the specified number of days added
   */
  addDays: (date: string | Date, days: number) =>
    dayjs(date).add(days, "day").toDate(),
};

/**
 * Generates time options in 30-minute increments, optionally starting from a given time string ("h:mm a").
 * @param startTime - Optional start time string in "h:mm a" format
 * @returns Array of { value, label } objects for time selection
 */
export const generateTimeOptions = (startTime?: string) => {
  const options: { value: string; label: string }[] = [];

  let start = 0;
  if (startTime) {
    const parsedTime = dayjs(startTime, "h:mm a", true);
    if (parsedTime.isValid()) {
      start = parsedTime.hour() * 60 + parsedTime.minute();
    }
  }

  for (let minutes = start; minutes < 24 * 60; minutes += 30) {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const period = hour >= 12 ? "pm" : "am";
    const displayHour = hour % 12 || 12;
    const displayMinute = minute.toString().padStart(2, "0");
    const label = `${displayHour}:${displayMinute} ${period}`;
    const value = dayjs().hour(hour).minute(minute).format("h:mm a");
    options.push({ value, label });
  }
  return options;
};

/**
 * Formats a pay period date range as a human-readable string
 *
 * Format: "MMM DD - MMM DD, YYYY"
 *
 * Example: "Apr 07 - Apr 13, 2025"
 *
 * @param start - Start date string (YYYY-MM-DD)
 * @param end - End date string (YYYY-MM-DD)
 * @returns Formatted range string
 */
export const formatDateRange = (start: string, end: string): string =>
  `${dayjs(start).format("MMM DD")} - ${dayjs(end).format("MMM DD, YYYY")}`;

/**
 * Sorts two date strings in descending order (most recent first)
 * @param dateA - First date string or null/undefined
 * @param dateB - Second date string or null/undefined
 * @returns Number for array sort comparison
 */
export const sortDateDescending = (
  dateA?: string | null,
  dateB?: string | null,
): number => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
};

/**
 * Sorts two date strings in ascending order (oldest first)
 * @param dateA - First date string or null/undefined
 * @param dateB - Second date string or null/undefined
 * @returns Number for array sort comparison
 */
export const sortDateAscending = (
  dateA?: string | null,
  dateB?: string | null,
): number => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  return dayjs(dateA).valueOf() - dayjs(dateB).valueOf();
};

/**
 * Formats a date with time, optionally including year if different from current year
 *
 * Format: "MMM D, h:mm A" for current year, "MMM D, YYYY, h:mm A" for other years
 *
 * Examples: "Sep 29, 3:05 PM", "Dec 15, 2023, 10:30 AM"
 *
 * @param date - Date string, Date object, or null
 * @returns Formatted date string or "No Date" if input is null/undefined
 */
export const formatActivityDate = (
  date?: string | Date | null,
  activityEvent?: SpiffRuleEvent,
) => {
  if (!date) return "No Date";

  const inputDate = dayjs(date);
  const now = dayjs();

  const isPricebookItem =
    activityEvent === SPIFF_RULE_EVENTS.pricebook_item_sale;
  const includeTime = !isPricebookItem;
  const sameYear = inputDate.isSame(now, "year");

  const baseFormat = sameYear ? "MMM D" : "MMM D, YYYY";
  const format = includeTime ? `${baseFormat}, h:mm A` : baseFormat;

  return inputDate.format(format);
};

/**
 * Calculates start and end dates based on a time frame identifier
 * @param timeFrame - Time frame identifier (e.g., 'last_thirty_days', 'all_time', 'week_1')
 * @returns Object containing start and end dates as dayjs objects, or null for 'all_time'
 */
export const getDateRangeFromTimeFrame = (
  timeFrame?: string,
): { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs } | null => {
  if (!timeFrame) return null;

  const now = dayjs();

  switch (timeFrame) {
    case "today": {
      const startDate = now.startOf("day");
      const endDate = now.endOf("day");
      return { startDate, endDate };
    }

    case "last_thirty_days": {
      const start = now.subtract(30, "day").startOf("day");
      return { startDate: start, endDate: now.endOf("day") };
    }

    case "last_month": {
      const lastMonth = now.subtract(1, "month");
      const startDate = lastMonth.startOf("month");
      const endDate = lastMonth.endOf("month");
      return { startDate, endDate };
    }

    case "all_time":
      return null;

    default: {
      if (!timeFrame.startsWith("week_")) return null;

      const weekNumber = parseInt(timeFrame.split("_")[1], 10);
      if (!Number.isInteger(weekNumber) || weekNumber <= 0) return null;

      const currentDay = now.day();
      const daysToLastMonday = currentDay === 0 ? 6 : currentDay - 1;
      const lastMonday = now.subtract(daysToLastMonday, "day").startOf("day");

      const weekStart = lastMonday
        .subtract((weekNumber - 1) * 7, "day")
        .startOf("day");
      const weekEnd = weekStart.add(6, "day").endOf("day");

      return { startDate: weekStart, endDate: weekEnd };
    }
  }
};

/**
 * Calculates the effective date string for spiff approvals
 * Returns undefined if the selected date is today (letting created_at handle it)
 * Otherwise returns formatted datetime with timezone offset
 *
 * @param selectedDate - The date selected for the approval
 * @param approvals - Array of previous approvals with display_date
 * @returns Formatted datetime string with timezone or undefined
 */
export const calculateEffectiveDateTime = (
  selectedDate: Date | null | undefined,
  approvals: Array<{ display_date: string }>,
): string | undefined => {
  if (!selectedDate) {
    return undefined;
  }

  const selected = dayjs(selectedDate).startOf("day");
  const today = dayjs().startOf("day");

  // If the selected date is today, don't set effective_at (let created_at handle it)
  if (selected.isSame(today, "day")) {
    return undefined;
  }

  // Find the most recent approval to check if we need to use its time
  if (approvals.length > 0) {
    const mostRecentApproval = approvals.reduce((latest, current) => {
      const currentDate = dayjs(current.display_date);
      const latestDate = dayjs(latest.display_date);
      return currentDate.isAfter(latestDate) ? current : latest;
    });

    const mostRecentDateTime = dayjs(mostRecentApproval.display_date);

    // If the selected date is the same day as the most recent approval, use that time
    if (selected.isSame(mostRecentDateTime, "day")) {
      return selected
        .hour(mostRecentDateTime.hour())
        .minute(mostRecentDateTime.minute())
        .second(mostRecentDateTime.second())
        .format(FORMAT_API_DATETIME);
    }
  }

  // Different day or no previous approvals, use beginning of day
  return selected.format(FORMAT_API_DATETIME);
};
